// src/APIs/waitlistApi.js
import api from "./axios";

// Provider view: waitlist for a single appointment (returns array)
export async function getProviderWaitlistForAppointment(
  providerId,
  appointmentId,
  { zone } = {},
) {
  const res = await api.get(
    `/api/v1/waitlist/providers/${providerId}/appointments/${appointmentId}`,
    { params: { zone } },
  );
  return res.data; // Array<WaitlistProviderViewDto>
}

/**
 * Get all waitlisted patients for a provider on a specific date
 * @param {number} providerId - The provider ID
 * @param {Object} params - Query parameters
 * @param {string} params.date - Date in YYYY-MM-DD format
 * @param {string} params.zone - Timezone (e.g., "America/New_York")
 * @returns {Promise<Array>} Array of waitlist entries with patient and appointment info
 */
export const getProviderWaitlistByDate = async (providerId, { date, zone }) => {
  const res = await api.get(
    `/api/v1/waitlist/providers/${providerId}/by-date`,
    {
      params: { date, zone },
    },
  );
  return res.data;
};

// List patient waitlisted appointments by date
export const listWaitlistedAppointmentsByDate = async (
  patientId,
  { date, zone } = {},
) => {
  const res = await api.get(`/api/v1/waitlist/patient/${patientId}/by-date`, {
    params: { date, zone },
  });
  return res.data; // expect an array of DTOs
};

export async function addToWaitlist(
  patientId,
  { appointmentId, providerId, reasonForWaitlist, allowProviderContact },
) {
  const url = `/api/v1/waitlist/patient/${patientId}/add`;
  const payload = {
    appointmentId,
    providerId,
    reasonForWaitlist,
    allowProviderContact,
  };
  const res = await api.post(url, payload);
  return res.data;
}

/**
 * Check if a patient already has a booked appointment.
 * Used to disable "Join Waitlist" if the patient already booked it.
 *
 * @returns {Promise<boolean>}
 */
export async function patientHasAppointment({
  providerId,
  appointmentId,
  patientId,
}) {
  const res = await api.get(
    `/api/v1/providers/${providerId}/appointments/${appointmentId}/patient/${patientId}/exists`,
  );

  return Boolean(res.data?.exists);
}

export async function listPatientWaitlist(patientId) {
  const res = await api.get(`/api/v1/waitlist/patient/${patientId}`);
  return res.data;
}

export async function removeFromWaitlist(patientIdOrObj, maybeWaitlistId) {
  // Support both signatures:
  // 1) removeFromWaitlist({ patientId, waitlistId })
  // 2) removeFromWaitlist(patientId, waitlistId)
  let patientId, waitlistId;

  if (typeof patientIdOrObj === "object" && patientIdOrObj !== null) {
    ({ patientId, waitlistId } = patientIdOrObj);
  } else {
    patientId = patientIdOrObj;
    waitlistId = maybeWaitlistId;
  }

  if (patientId == null || waitlistId == null) {
    throw new Error(
      "removeFromWaitlist: patientId and waitlistId are required",
    );
  }

  const url = `/api/v1/waitlist/patient/${patientId}/remove/${waitlistId}`;
  const res = await api.delete(url);
  // Some setups return 204, some 200 with empty body—treat both as success
  return res.status === 204 || res.status === 200;
}
